import { ColumnDef } from '@tanstack/react-table';
import { EllipsisVerticalIcon } from 'lucide-react';
import { toast } from 'sonner';

import { RouteDetailsSheet } from '@/app/(app)/routes/details-sheet';
import { EditRouteDialog } from '@/app/(app)/routes/dialog';
import { DateTimeInUTC } from '@/components/atoms/datetime';
import { Link } from '@/components/link';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useEmployeeById, useIssuesById, useRegionById, useSkillById } from '@/components/providers/v1-office-hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ApiRoute = ApiSchemas['RouteRead'];

export const ROUTE_COLUMNS: ColumnDef<ApiRoute>[] = [
  {
    id: 'day',
    header: 'Day',
    cell: ({ row }) => {
      return <DateTimeInUTC value={row.original.start_at} format='EEE, d MMM' />;
    },
  },
  {
    accessorKey: 'employee_id',
    header: 'Employee',
    cell: ({ row }) => {
      return <EmployeeCell employee_id={row.original.employee_id} />;
    },
  },
  {
    accessorKey: 'bookable',
    header: 'Bookable',
    cell: ({ row }) => <BookableCell employee_id={row.original.employee_id} />,
  },
  {
    accessorKey: 'regions',
    header: 'Regions',
    cell: ({ row }) => <RouteRegions route={row.original} />,
  },
  {
    accessorKey: 'skills',
    header: 'Skills',
    cell: ({ row }) => <RouteSkills route={row.original} />,
  },
  {
    accessorKey: 'events',
    header: 'Appointments',
    cell: ({ row }) => {
      return <Badge variant='secondary'>{row.original.event_count}</Badge>;
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return <ActionCell route={row.original} />;
    },
  },
];

function BookableCell({ employee_id }: { employee_id: string | null | undefined }) {
  const employee = useEmployeeById(employee_id);
  if (!employee) return null;
  return <Badge variant={employee.bookable ? 'default' : 'secondary'}>{employee.bookable ? 'Yes' : 'No'}</Badge>;
}

function EmployeeCell({ employee_id }: { employee_id: string | null | undefined }) {
  const employee = useEmployeeById(employee_id);
  if (!employee) return null;
  return (
    <Link href={`/settings/employees/${employee_id}`} className='inline-block font-medium hover:underline'>
      {employee.first_name} {employee.last_name}
    </Link>
  );
}

function RouteRegions({ route }: { route: ApiRoute }) {
  const employee = useEmployeeById(route.employee_id);
  if (!employee) return null;

  const notShown = employee.employee_regions.length - 3;
  return (
    <div className='flex max-w-64 min-w-24 flex-wrap gap-2'>
      {employee.employee_regions.slice(0, 3).map((region) => (
        <RegionBadge key={region.office_region_id} region_id={region.office_region_id} />
      ))}
      {notShown > 0 && <Badge variant='outline'>+{notShown}</Badge>}
    </div>
  );
}

function RegionBadge({ region_id }: { region_id: string | null | undefined }) {
  const region = useRegionById(region_id);
  if (!region) return null;
  return <Badge variant='outline'>{region.name}</Badge>;
}

function RouteSkills({ route }: { route: ApiRoute }) {
  const employee = useEmployeeById(route.employee_id);
  if (!employee) return null;

  const notShown = employee.employee_skills.length - 3;
  return (
    <div className='flex max-w-64 min-w-24 flex-wrap gap-2'>
      {employee.employee_skills.slice(0, 3).map((skill) => (
        <SkillBadge key={skill.skill_id} skill_id={skill.skill_id} />
      ))}
      {notShown > 0 && <Badge variant='outline'>+{notShown}</Badge>}
    </div>
  );
}

function SkillBadge({ skill_id }: { skill_id: string | null | undefined }) {
  const skill = useSkillById(skill_id);
  if (!skill) return null;
  return <Badge variant='outline'>{skill.name}</Badge>;
}

function IssuesCell({ issue_id }: { issue_id: string | null | undefined }) {
  const issue = useIssuesById(issue_id);
  if (!issue) return null;
  return <Badge variant='outline'>{issue.name}</Badge>;
}

interface ActionCellProps {
  route: ApiRoute;
}

function ActionCell({ route }: ActionCellProps) {
  const { api } = useApiV1Ctx();
  const { mutate } = api.routes.deleteRoutesRouteIdDelete.useMutation(
    {
      path: { route_id: route.id },
    },
    {
      onSuccess: () => {
        toast.success('Route deleted');
        api.routes.searchRoutesSearchGet.invalidateQueries();
      },
    }
  );

  const onDelete = () => {
    if (confirm('Are you sure you want to delete this route?')) {
      mutate();
    }
  };

  return (
    <div className='flex justify-end'>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='icon'>
            <EllipsisVerticalIcon className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <RouteDetailsSheet
            routeId={route.id}
            trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Details</DropdownMenuItem>}
          />
          <EditRouteDialog
            route={route}
            trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Edit</DropdownMenuItem>}
          />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onDelete} variant='destructive'>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
